import React, { useCallback, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { FullPageLoadingScreen } from '@confluence/full-page-loading-screen';
import { useNativeCollabPresenceState } from '@confluence/native-collab/entry-points/useNativeCollabPresenceState.ts';
import {
	nativeCollabPermissionsTokenState,
	fetchCollabPermissionToken,
} from '@confluence/native-collab/entry-points/CollabPermissionsToken.ts';
import { useNativeCollabActions } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useMatchRoute } from '@confluence/route-manager/entry-points/RouteState.ts';
import { EDIT_PAGE_EMBED } from '@confluence/named-routes/entry-points/routes.ts';
import type { InitialDraft } from '@confluence/fabric-providers/entry-points/fetchDraft.ts';
import { getCollabEditProvider } from '@confluence/fabric-providers/entry-points/NativeCollabProvider.ts';
import { COLLAB_EDIT_PROVIDER_MEASURES } from '@confluence/fabric-providers/entry-points/CollabEditProviderMeasures.ts';
import { getDraft } from '@confluence/fabric-providers/entry-points/getDraft.ts';
import { useFetchUsersClient } from '@confluence/fabric-providers/entry-points/useFetchUsersClient.ts';
import { CollabEditProviderMeasures } from '@confluence/action-measures/entry-points/CollabEditProviderMeasures.ts';
import { useEditSource } from '@confluence/edit-common/entry-points/useEditSource.ts';

import { isEditSourceInitialLoad } from './checkEditSourceType.ts';
import {
	extractIsEditorVersionFabric,
	fetchEditorTypeAndVersionPropertyQuery,
} from './editorTypeAndVersionPropertyHelpers.tsx';
import type { EditorTypeAndVersionPropertyQuery } from './preload/queries/__types__/EditorTypeAndVersionPropertyQuery.ts';

export const PRELOAD_PERMS_TOKEN_ON_HOVER_TIMEOUT = 3000;

const createAndFireErrorEvent = ({ contentId, action, reason, createAnalyticsEvent }) => {
	createAnalyticsEvent({
		type: 'sendOperationalEvent',
		data: {
			actionSubject: 'editor',
			action: `preload.${action}.error`,
			source: 'editPageLoadingComponent',
			objectId: contentId,
			attributes: {
				...(reason ? { error: reason } : {}),
			},
		},
	}).fire();
};

type Props = {
	contentId: string;
	spaceKey: string | undefined;
	redirectUrl: string;
	contentType?: string | null;
	redirectToEditor: (url: string) => void;
	nativeEditorComponentsPreloader: () => Promise<void>;
};

export const EditPageLoadingComponent = ({
	contentId,
	spaceKey,
	redirectUrl,
	contentType,
	redirectToEditor,
	nativeEditorComponentsPreloader,
}: Props) => {
	const editPageTimingStart = performance.now();
	const draftShareId = new URLSearchParams(redirectUrl).get('draftShareId') || '';

	const matchRoute = useMatchRoute();
	const [{ editSource }] = useEditSource();
	const { userId, cloudId, activationId } = useSessionData();
	const { client: fetchUsersClient } = useFetchUsersClient();

	const isEmbeddedRoute = matchRoute(redirectUrl)?.name === EDIT_PAGE_EMBED.name;

	const { setCollabEditProvider, setHasInitialDraft } = useNativeCollabActions();

	// presenceId is pulled from the native collab state to ensure that we have a consistent presenceId for a given contentId
	// across the collab providers that drive editor as well as presence components (telepointer and avatar colors).
	const [{ presenceId }] = useNativeCollabPresenceState();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isNCS = true; // these conditions guarantee that page is NCS. If migration is enabled or is the page is embedded.

	const getNCSCollabProvider = useCallback(
		async (draft: InitialDraft | null) => {
			if (isEmbeddedRoute || !contentType || isEditSourceInitialLoad(editSource)) {
				return;
			}
			// If the token has already been fetched and the draft exists, the permissions token has been used by the draft
			const permissionsTokenState =
				nativeCollabPermissionsTokenState.getPermissionsTokenState(contentId);
			if (permissionsTokenState) {
				const { getToken, isUsedForDraftFetch } = permissionsTokenState;
				if (!isUsedForDraftFetch && draft) {
					const token = await getToken;
					if (token) {
						nativeCollabPermissionsTokenState.setIsUsedForDraftFetch(contentId, true);
					}
				}
			}

			// DO NOT add any asynchronous calls/behavior to the collab provider creation. This has caused incidents
			// in the past where the provider promise never resolves and editing breaks.
			// Context: https://hello.atlassian.net/wiki/spaces/~589311246/pages/2554442259/Investigate+async+issue+HOT-103180
			const ncsProvider = getCollabEditProvider({
				contentId,
				cloudId,
				activationId,
				contentType,
				permissionTokenRefresh: fetchCollabPermissionToken(contentId, draftShareId),
				initialDraft: draft,
				presenceId,
				presenceActivity: 'editor',
				userId,
				fetchUsersClient,
			});
			setCollabEditProvider(ncsProvider);

			// @ts-ignore
			const hasInitialDraft = Boolean(ncsProvider?.initialDraft);
			setHasInitialDraft(hasInitialDraft);

			// marking provider creation
			CollabEditProviderMeasures.markMeasureEnd(
				COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_CREATED,
			);
			// at this point, provider has been created but is not yet initialized
			CollabEditProviderMeasures.markMeasureStart(
				COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_DISABLED,
			);
			// marking post-creation -> pre-connection time
			CollabEditProviderMeasures.markMeasureStart(
				COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_PRE_CONNECTED,
			);

			// track number of early provider creations with successful initial draft
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					actionSubject: 'editor',
					action: 'ncsCollabProviderCreated',
					source: 'editPageLoadingComponent',
					attributes: {
						contentId,
						editSource,
						hasInitialDraft,
					},
				},
			}).fire();
		},
		[
			activationId,
			contentId,
			cloudId,
			draftShareId,
			setCollabEditProvider,
			setHasInitialDraft,
			createAnalyticsEvent,
			editSource,
			contentType,
			isEmbeddedRoute,
			presenceId,
			userId,
			fetchUsersClient,
		],
	);

	const preloadEditorAndRedirect = useCallback(async () => {
		// prettier-ignore
		let isFabric = true;
		// while we are supporting both synchrony and NCS, we need to make a blocking query to determine which resources should be preloaded.
		// query depends on the content id for the page that the user has decided to edit
		// short circuit if we already know that the editor must be NCS from isEmbeddedRoute, otherwise fetch from content property
		if (!isEmbeddedRoute) {
			// Order of promises is important here to be able to extract properties below correctly.
			const promises = [
				fetchEditorTypeAndVersionPropertyQuery({
					contentId,
					createAnalyticsEvent,
				}),
			];

			const [editorTypeAndVersionPropertyQueryResult] = await Promise.allSettled(promises);

			if (
				editorTypeAndVersionPropertyQueryResult.status === 'fulfilled' &&
				editorTypeAndVersionPropertyQueryResult.value
			) {
				isFabric = extractIsEditorVersionFabric(
					editorTypeAndVersionPropertyQueryResult.value as EditorTypeAndVersionPropertyQuery,
				);
			} else if (editorTypeAndVersionPropertyQueryResult.status === 'rejected') {
				createAndFireErrorEvent({
					contentId,
					action: 'fetchEditorTypeAndVersionPropertyQuery',
					reason: editorTypeAndVersionPropertyQueryResult.reason,
					createAnalyticsEvent,
				});
			}
		}

		const fireTimingAnalytics = () => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					actionSubject: 'editor',
					action: 'edit.page.loading.component.timing',
					source: 'editPageLoadingComponent',
					attributes: {
						timing: performance.now() - editPageTimingStart,
						collabService: 'ncs',
						isFabricEditor: isFabric,
						contentId,
					},
				},
			}).fire();
		};

		const firePermsTokenAnalytics = (preloadTokenSuccess: boolean) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					actionSubject: 'editor',
					action: 'preloadNCSPermsToken',
					source: 'editPageLoadingComponent',
					attributes: {
						contentId,
						preloadTokenSuccess,
					},
				},
			}).fire();
		};

		const isTokenStale = (stopTime: number, timestamp: number) => {
			if (timestamp > stopTime) {
				// if token promise resolves after user click
				return false;
			}
			return stopTime - timestamp > PRELOAD_PERMS_TOKEN_ON_HOVER_TIMEOUT;
		};

		const getPermissionsToken = async (contentId: string, draftShareId: string) => {
			const stopTime = performance.now();
			const permissionsTokenState =
				nativeCollabPermissionsTokenState.getPermissionsTokenState(contentId);

			if (permissionsTokenState?.getToken) {
				// <-- HOVER SCENARIOS -->
				// await pending promise and return the hovered token result only in the case where:
				// 1. token is null (some error occurred)
				// 2. token promise resolved AFTER user click (timestamp will be > user click stop time)
				// 2. token was already resolved and its timestamp is <= the FF timeout at the time of click
				const token = await permissionsTokenState.getToken;
				if (
					!token ||
					(permissionsTokenState.timestamp &&
						!isTokenStale(stopTime, permissionsTokenState.timestamp))
				) {
					return token;
				}
			}
			// Fetch the token as it has not been previously been requested or is stale
			const token = await fetchCollabPermissionToken(contentId, draftShareId)();
			// token can potentially be null if fetchCollabPermissionToken throws an error, so only set it in state if it's valid
			if (token) {
				nativeCollabPermissionsTokenState.setToken(contentId, Promise.resolve(token));
				nativeCollabPermissionsTokenState.setTimestamp(contentId);
			}
			return token;
		};

		if (isFabric) {
			let getDraftPromise: Promise<InitialDraft | null> = Promise.resolve(null);

			// existingDraft will only exist if draft is fetched on hover, otherwise draft is requested before provider creation (on click)
			if (!isEmbeddedRoute && contentType && !isEditSourceInitialLoad(editSource)) {
				const token = await getPermissionsToken(contentId, draftShareId);
				getDraftPromise = getDraft(contentId);
				firePermsTokenAnalytics(Boolean(token));
			}
			await Promise.all([nativeEditorComponentsPreloader(), getDraftPromise]).then(
				async ([, draftResponse]) => {
					fireTimingAnalytics();
					await getNCSCollabProvider(draftResponse);
				},
			);
		}

		redirectToEditor(redirectUrl);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		nativeEditorComponentsPreloader,
		contentId,
		spaceKey,
		userId,
		draftShareId,
		redirectToEditor,
		redirectUrl,
		isNCS,
		createAnalyticsEvent,
		isEmbeddedRoute,
	]);

	useEffect(() => {
		void preloadEditorAndRedirect();
	}, [preloadEditorAndRedirect]);

	return <FullPageLoadingScreen />;
};
